'use client';

import ImageSB, { ImageSBProps } from '@/components/atoms/image/ImageSB';
import LinkButtonSB, {
  LinkButtonSBProps,
} from '@/components/atoms/linkButton/LinkSB';
import RichTextFieldSB, {
  RichTextFieldSBProps,
} from '@/components/atoms/richTextField/RichTextFieldSB';
import {
  ToggleCMSProps,
  useCMSToggle,
} from '@/components/atoms/toggle/useCMSToggle';
import VideoSB, { VideoSBProps } from '@/components/atoms/video/VideoSB';
import { BlockComponentModel } from '@/components/cms/types/blok-component';

import {
  BlockHeader,
  BlockHeaderContent,
} from '@/features/utils/BlockHeaderContent';
import { BlockWrapper } from '@/features/utils/BlockWrapper';

import { cn } from '@/lib/utils';
import ProductCardItem, { ProductCardItemProps } from './ProductCardItem';

export interface CardsWithHeaderStrapiProps {
  header?: BlockHeader;
  toggle?: ToggleCMSProps;
  cards: {
    icon: ImageSBProps;
    title: string;
    subtitle?: string;
    description: RichTextFieldSBProps;
    button: LinkButtonSBProps;
    videoLink: VideoSBProps;
    documentLink?: string;
    cardType: ProductCardItemProps['cardType'];
  }[];
}

export default function ProductCardsStrapi({
  block,
  ...rest
}: BlockComponentModel<CardsWithHeaderStrapiProps>) {
  const { cards, header, toggle } = block;
  const { filteredToggleList, toggleNode: ToggleNode } = useCMSToggle<
    typeof cards
  >(cards, toggle);

  return (
    <BlockWrapper {...rest} theme={header?.theme?.themeKey}>
      <div data-tracker-section='product-cards' className='flex'>
        <BlockHeaderContent
          {...header}
          topChildren={ToggleNode ? ToggleNode() : undefined}
        >
          <div className='flex grid-cols-1 flex-wrap gap-5 md:grid md:grid-cols-2 md:gap-[23px] lg:grid-cols-3'>
            {cards.map((card, index) => (
              <div
                key={card.title}
                className={cn(
                  'w-full',
                  filteredToggleList?.length &&
                    !filteredToggleList?.some(
                      (item) => card.title === item.title
                    ) &&
                    'hide-content'
                )}
              >
                <ProductCardItem
                  key={`${card.title}`}
                  icon={
                    <ImageSB
                      block={{ ...card.icon }}
                      inner={{ classNames: { image: 'w-9' } }}
                    />
                  }
                  title={card.title}
                  subtitle={card.subtitle}
                  description={
                    card.description && (
                      <div data-tracker-component='description'>
                        <RichTextFieldSB
                          block={card.description}
                          classNames={{ paragraph: 'text-body-l' }}
                        />
                      </div>
                    )
                  }
                  button={
                    card.button ? (
                      <LinkButtonSB
                        block={card?.button}
                        inner={{
                          slim: false,
                          variant: 'outlined',
                          color: 'secondary',
                          classNames: {
                            inner: 'justify-center',
                            root: 'min-w-[153px]',
                          },
                        }}
                      />
                    ) : undefined
                  }
                  videoLink={
                    card.videoLink ? (
                      <VideoSB block={card.videoLink} />
                    ) : undefined
                  }
                  documentLink={
                    card.documentLink ? card.documentLink : undefined
                  }
                  cardType={card.cardType}
                />
              </div>
            ))}
          </div>
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
}
